import Vue from 'vue';

const USER_LOCALE_STORAGE_KEY = 'coapp-user-language';
const DEFAULT_LOCALE = 'en-US';
const GERMAN_LOCALE = 'de-DE';

function canUseLocalStorage() {
    try {
        const testKey = '__coapp_locale_test__';
        window.localStorage.setItem(testKey, testKey);
        window.localStorage.removeItem(testKey);
        return true;
    } catch (error) {
        return false;
    }
}

export function normalizeAppLocale(locale) {
    const normalizedLocale = String(locale || '')
        .trim()
        .toLowerCase();

    if (normalizedLocale.startsWith('de')) {
        return GERMAN_LOCALE;
    }

    return DEFAULT_LOCALE;
}

export function getPersistedUserLocale() {
    if (!canUseLocalStorage()) {
        return '';
    }

    const persistedLocale = window.localStorage.getItem(USER_LOCALE_STORAGE_KEY);

    if (!persistedLocale) {
        return '';
    }

    return normalizeAppLocale(persistedLocale);
}

export function persistUserLocale(locale) {
    if (!canUseLocalStorage()) {
        return '';
    }

    const normalizedLocale = normalizeAppLocale(locale);
    window.localStorage.setItem(USER_LOCALE_STORAGE_KEY, normalizedLocale);

    return normalizedLocale;
}

export function clearPersistedUserLocale() {
    if (!canUseLocalStorage()) {
        return;
    }

    window.localStorage.removeItem(USER_LOCALE_STORAGE_KEY);
}

export function setDocumentLocale(locale) {
    if (typeof document === 'undefined' || !document.documentElement) {
        return;
    }

    document.documentElement.setAttribute('lang', normalizeAppLocale(locale));
}

export function resolveInitialUserLocale({ userLocale = '', persistedLocale = '', navigatorLocale = '' } = {}) {
    return normalizeAppLocale(userLocale || persistedLocale || navigatorLocale);
}

export function applyUserLocale(target, locale, { persist = false } = {}) {
    const normalizedLocale = normalizeAppLocale(locale);

    // Update the shared prototype value so components mounted later read the same locale.
    Vue.prototype.$language = normalizedLocale;

    if (target && target.$i18n) {
        target.$i18n.locale = normalizedLocale;
    }

    if (target) {
        target.$language = normalizedLocale;
    }

    if (persist) {
        persistUserLocale(normalizedLocale);
    }

    // Keep the DOM language in sync so components mounted later inherit the right locale context.
    setDocumentLocale(normalizedLocale);

    return normalizedLocale;
}
